import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { APIupdateSprkImage } from '../../../api/Assets';

import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type WaSelect from '@awesome.me/webawesome/dist/components/select/select.js';
import type { Asset } from '../../../types/asset';
import type { AssetGroup } from '../../../types/asset-group';

import '@awesome.me/webawesome/dist/components/select/select.js';

interface AssetEditModalProps {
  asset: Asset;
  assetGroups: AssetGroup[];
  show: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
};

const AssetEditModal: React.FC<AssetEditModalProps> = ({ asset, assetGroups, show, onConfirm, onDismiss }) => {
  const [title, setTitle] = useState(asset.title ?? '');
  const [groups, setGroups] = useState(() => asset.groups?.map(group => String(group.id)) ?? []);
  const [hidden, setHidden] = useState(asset.metadata?.hidden ?? false);
  const dialogRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const { mutate: save, isPending: saving, error } = useMutation({
    mutationFn: () => APIupdateSprkImage(asset.id, { title, groups, metadata: { hidden } }),
    onSuccess: onConfirm,
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    const handleHide = (event: Event) => {
      if (event.target !== dialog) {
        return;
      }
      if (saving) {
        event.preventDefault();
      } else {
        onDismiss();
      }
    };
    dialog?.addEventListener('wa-hide', handleHide);
    return () => dialog?.removeEventListener('wa-hide', handleHide);
  }, [saving, onDismiss]);

  return (
    <wa-dialog ref={(dialog) => { dialogRef.current = dialog; }} label="Edit SPRK image" id="dialog-edit-asset" open={show}>
      <form
        ref={formRef}
        className="wa-stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!saving) {
            save();
          }
        }}
      >
        <div className="wa-stack wa-gap-xs">
          <label htmlFor="asset-title">Image title</label>
          <input id="asset-title" name="title" value={title} disabled={saving} onChange={(event) => setTitle(event.target.value)} />
        </div>
        <wa-select
          label="Groups"
          name="groups"
          multiple
          withClear
          placeholder="Select groups"
          value={groups}
          disabled={saving}
          onChange={(event) => {
            const value = (event.target as WaSelect).value;
            setGroups(Array.isArray(value) ? value : value ? [value] : []);
          }}
        >
          {assetGroups.map(group => (
            <wa-option key={group.id} value={String(group.id)}>{group.name}</wa-option>
          ))}
        </wa-select>
        <wa-checkbox
          name="hidden"
          checked={hidden}
          disabled={saving}
          onChange={(event) => setHidden((event.target as WaCheckbox).checked)}
        >
          Hidden
        </wa-checkbox>
        {error && <div className="error-message" role="alert">{error.message || 'Failed to update image. Please try again.'}</div>}
      </form>
      <wa-button slot="footer" size="s" appearance="outlined" disabled={saving} onClick={onDismiss}>
        Cancel
      </wa-button>
      <wa-button slot="footer" size="s" variant="brand" disabled={saving} onClick={() => formRef.current?.requestSubmit()}>
        {saving ? 'Saving...' : 'Save changes'}
      </wa-button>
    </wa-dialog>
  );
}

export default AssetEditModal;
